'use client';

export default function GuidesPage() {
  return (
    <div className="relative min-h-screen overflow-hidden bg-gradient-to-br from-slate-50 via-blue-50/30 to-indigo-50/20 dark:from-slate-950 dark:via-blue-950/30 dark:to-indigo-950/20">
      {/* Animated background orbs */}
      <div className="absolute top-0 right-1/4 w-96 h-96 bg-gradient-to-r from-blue-400 to-cyan-400 rounded-full mix-blend-multiply filter blur-3xl opacity-20 dark:opacity-10 animate-blob" />
      <div className="absolute bottom-0 left-1/4 w-96 h-96 bg-gradient-to-r from-purple-400 to-pink-400 rounded-full mix-blend-multiply filter blur-3xl opacity-20 dark:opacity-10 animate-blob animation-delay-2000" />

      <div className="relative container mx-auto px-4 py-16 pt-32 max-w-5xl">
        {/* Hero Section */}
        <div className="text-center mb-16">
          <h1 className="text-5xl md:text-7xl font-black bg-gradient-to-r from-slate-900 via-blue-900 to-indigo-900 dark:from-white dark:via-blue-100 dark:to-indigo-100 bg-clip-text text-transparent leading-tight mb-6">
            Guides & Tutorials
          </h1>
          <p className="text-2xl text-slate-600 dark:text-slate-400 leading-relaxed max-w-3xl mx-auto">
            Step-by-step tutorials for common backup scenarios and advanced configurations.
          </p>
        </div>

        {/* Content */}
        <div className="relative">
          <div className="absolute -inset-4 bg-white/40 dark:bg-slate-900/40 backdrop-blur-3xl rounded-2xl -z-10" />
          <div className="prose prose-lg prose-slate dark:prose-invert max-w-none">
            
            <h2 className="text-3xl font-black text-slate-900 dark:text-white mt-10 mb-5 flex items-center gap-3">
              <span className="w-2 h-8 bg-gradient-to-b from-blue-600 to-indigo-600 rounded-full" />
              Backup Strategies
            </h2>

            <h3 className="text-2xl font-bold text-slate-900 dark:text-white mt-8 mb-4">Setting Up the 3-2-1 Backup Rule</h3>
            <p className="text-lg leading-relaxed text-slate-700 dark:text-slate-300 mb-6">
              The 3-2-1 rule is a backup best practice: 3 copies of data, on 2 different media, with 1 copy offsite.
            </p>
            <ol className="space-y-3 mb-8 ml-6">
              <li className="text-lg text-slate-700 dark:text-slate-300 pl-2">
                <span className="inline-flex items-start gap-3">
                  <span className="w-1.5 h-1.5 rounded-full bg-gradient-to-r from-blue-600 to-indigo-600 mt-3 flex-shrink-0" />
                  <span><strong>Primary copy:</strong> Your production database</span>
                </span>
              </li>
              <li className="text-lg text-slate-700 dark:text-slate-300 pl-2">
                <span className="inline-flex items-start gap-3">
                  <span className="w-1.5 h-1.5 rounded-full bg-gradient-to-r from-blue-600 to-indigo-600 mt-3 flex-shrink-0" />
                  <span><strong>Local backup:</strong> pgBackRest on local storage</span>
                </span>
              </li>
              <li className="text-lg text-slate-700 dark:text-slate-300 pl-2">
                <span className="inline-flex items-start gap-3">
                  <span className="w-1.5 h-1.5 rounded-full bg-gradient-to-r from-blue-600 to-indigo-600 mt-3 flex-shrink-0" />
                  <span><strong>Remote backup:</strong> Replicate to cloud storage (S3, Azure Blob)</span>
                </span>
              </li>
            </ol>
            
            <div className="relative group my-8">
              <div className="absolute -inset-1 bg-gradient-to-r from-blue-600 to-indigo-600 rounded-xl blur opacity-25 group-hover:opacity-40 transition" />
              <pre className="relative bg-slate-900 dark:bg-slate-950 text-slate-100 p-6 rounded-xl overflow-x-auto border border-slate-700/50">
                <code>{`# pgbackrest.conf
[main]
pg1-path=/var/lib/postgresql/14/main

[global]
repo1-path=/backup/local
repo1-retention-full=7

repo2-type=s3
repo2-s3-bucket=my-backups
repo2-s3-region=us-east-1
repo2-retention-full=30`}</code>
              </pre>
            </div>

            <h3 className="text-2xl font-bold text-slate-900 dark:text-white mt-8 mb-4">Implementing Point-in-Time Recovery (PITR)</h3>
            <p className="text-lg leading-relaxed text-slate-700 dark:text-slate-300 mb-6">
              PITR allows you to restore your database to any specific moment in time.
            </p>

            <h2 className="text-3xl font-black text-slate-900 dark:text-white mt-10 mb-5 flex items-center gap-3">
              <span className="w-2 h-8 bg-gradient-to-b from-blue-600 to-indigo-600 rounded-full" />
              Performance Optimization
            </h2>

            <h3 className="text-2xl font-bold text-slate-900 dark:text-white mt-8 mb-4">Parallel Backup Processing</h3>
            <p className="text-lg leading-relaxed text-slate-700 dark:text-slate-300 mb-6">
              Speed up large database backups using parallel processing:
            </p>
            <div className="relative group my-8">
              <div className="absolute -inset-1 bg-gradient-to-r from-blue-600 to-indigo-600 rounded-xl blur opacity-25 group-hover:opacity-40 transition" />
              <pre className="relative bg-slate-900 dark:bg-slate-950 text-slate-100 p-6 rounded-xl overflow-x-auto border border-slate-700/50">
                <code>{`pgbackrest --stanza=main --type=full \\
  --process-max=4 backup`}</code>
              </pre>
            </div>

            <h2 className="text-3xl font-black text-slate-900 dark:text-white mt-10 mb-5 flex items-center gap-3">
              <span className="w-2 h-8 bg-gradient-to-b from-blue-600 to-indigo-600 rounded-full" />
              Disaster Recovery
            </h2>

            <h3 className="text-2xl font-bold text-slate-900 dark:text-white mt-8 mb-4">Creating a DR Runbook</h3>
            <p className="text-lg leading-relaxed text-slate-700 dark:text-slate-300 mb-6">
              Document your disaster recovery procedures:
            </p>
            <ol className="space-y-3 mb-8 ml-6">
              <li className="text-lg text-slate-700 dark:text-slate-300 pl-2">
                <span className="inline-flex items-start gap-3">
                  <span className="w-1.5 h-1.5 rounded-full bg-gradient-to-r from-blue-600 to-indigo-600 mt-3 flex-shrink-0" />
                  <span><strong>Identify critical systems</strong> and their RPO/RTO requirements</span>
                </span>
              </li>
              <li className="text-lg text-slate-700 dark:text-slate-300 pl-2">
                <span className="inline-flex items-start gap-3">
                  <span className="w-1.5 h-1.5 rounded-full bg-gradient-to-r from-blue-600 to-indigo-600 mt-3 flex-shrink-0" />
                  <span><strong>Document restore procedures</strong> with exact commands</span>
                </span>
              </li>
              <li className="text-lg text-slate-700 dark:text-slate-300 pl-2">
                <span className="inline-flex items-start gap-3">
                  <span className="w-1.5 h-1.5 rounded-full bg-gradient-to-r from-blue-600 to-indigo-600 mt-3 flex-shrink-0" />
                  <span><strong>Test quarterly</strong> with full restore drills</span>
                </span>
              </li>
              <li className="text-lg text-slate-700 dark:text-slate-300 pl-2">
                <span className="inline-flex items-start gap-3">
                  <span className="w-1.5 h-1.5 rounded-full bg-gradient-to-r from-blue-600 to-indigo-600 mt-3 flex-shrink-0" />
                  <span><strong>Maintain contact list</strong> for emergency escalation</span>
                </span>
              </li>
            </ol>

            <h2 className="text-3xl font-black text-slate-900 dark:text-white mt-10 mb-5 flex items-center gap-3">
              <span className="w-2 h-8 bg-gradient-to-b from-blue-600 to-indigo-600 rounded-full" />
              Security Best Practices
            </h2>

            <h3 className="text-2xl font-bold text-slate-900 dark:text-white mt-8 mb-4">Encrypting Backups</h3>
            <p className="text-lg leading-relaxed text-slate-700 dark:text-slate-300 mb-6">
              Protect sensitive data with encryption:
            </p>
            <div className="relative group my-8">
              <div className="absolute -inset-1 bg-gradient-to-r from-blue-600 to-indigo-600 rounded-xl blur opacity-25 group-hover:opacity-40 transition" />
              <pre className="relative bg-slate-900 dark:bg-slate-950 text-slate-100 p-6 rounded-xl overflow-x-auto border border-slate-700/50">
                <code>{`# pgbackrest.conf
[global]
repo1-cipher-type=aes-256-cbc
repo1-cipher-pass=<your-encryption-key>`}</code>
              </pre>
            </div>

            <div className="relative mt-12 mb-8">
              <div className="absolute -inset-1 bg-gradient-to-r from-yellow-400 to-orange-400 rounded-xl blur opacity-20" />
              <div className="relative bg-gradient-to-br from-yellow-50 to-orange-50 dark:from-yellow-950/50 dark:to-orange-950/50 border border-yellow-200 dark:border-yellow-800 rounded-xl p-8">
                <h3 className="text-2xl font-black text-yellow-900 dark:text-yellow-100 mb-4">💡 Pro Tip</h3>
                <p className="text-lg text-yellow-900 dark:text-yellow-100 mb-0">
                  Always test your restore procedures before you need them. Schedule quarterly DR drills to ensure your team knows the process and your backups are reliable.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <style jsx>{`
        @keyframes blob {
          0%, 100% { transform: translate(0, 0) scale(1); }
          33% { transform: translate(30px, -50px) scale(1.1); }
          66% { transform: translate(-20px, 20px) scale(0.9); }
        }
        .animate-blob {
          animation: blob 7s infinite;
        }
        .animation-delay-2000 {
          animation-delay: 2s;
        }
      `}</style>
    </div>
  );
}
